import type { CSSProperties } from 'react'

/**
 * Crops a full card scan down to its ART BOX.
 *
 * Scryfall serves whole cards, frame and all. `object-cover` on a wide box
 * scales a 488x680 card by WIDTH, so the black border and the gold frame edge
 * stay in shot and a deck banner reads as a photo of a card rather than as
 * art. At 56px the frame is too small to notice; at 400px wide it is the first
 * thing you see.
 *
 * Measured against the `normal` scan (488x680), whose art box is roughly
 * x 30..458 and y 60..300. Everything below is derived from those four
 * numbers, so a frame change means re-measuring here and nowhere else.
 *
 * The image is absolutely positioned and oversized inside an
 * `overflow-hidden` parent rather than driven by `object-position`, because
 * object-fit cannot zoom past "cover" and the art box needs a tighter crop
 * than cover produces.
 *
 * Full-art and borderless printings have no frame to trim, so they land
 * slightly zoomed. That is the right failure: a little too close on the
 * exception beats a visible frame on the rule.
 */

const CARD_W = 488
const CARD_H = 680
// The measured art box on a standard frame.
const ART_X = 30
const ART_W = 428
const ART_Y = 60
const ART_H = 240

/**
 * Trimmed off every edge of the art box before cropping.
 *
 * Not every printing puts its art in the same place. Showcase, Marvel and
 * Universes Beyond frames run a few pixels tighter or wider than the standard
 * one, and cropping exactly to the measured box left a bright sliver of frame
 * down one edge of about one card in six. Losing 8px of art on every card is
 * invisible; a frame sliver on some cards is not.
 */
const SAFETY = 8

/** Card aspect as a multiplier from rendered width to rendered height. */
const CARD_RATIO = CARD_H / CARD_W

/** A window onto the card, in card pixels on the 488x680 `normal` scan. */
export interface ArtSafeBox {
  x: number
  y: number
  w: number
  h: number
}

/**
 * The default safe box: the measured art box inset by SAFETY on every side
 * (y 68..292). Fine where the container is WIDER than the art box, so width
 * is the limit and the window is centred well clear of the top and bottom.
 */
export const DEFAULT_ART_SAFE_BOX: ArtSafeBox = {
  x: ART_X + SAFETY,
  y: ART_Y + SAFETY,
  w: ART_W - SAFETY * 2,
  h: ART_H - SAFETY * 2,
}

/**
 * The tighter box for containers proportionally TALLER than the art box (the
 * deck-tile crops in CardStack). There height is the limit, so the window
 * spans the box's full height, and the default box's top (y 68, 10% of the
 * card) sits above where most real printings' art starts: a hairline of card
 * frame showed across the top of nearly every My Decks tile (Keith
 * 2026-10-16). This is y 13.5%..45.5%, x 8%..92% of the card, a point inside
 * the 12.5%..46.5% / 8%..92% "safe" box that scripts/validate-art-crops.mjs
 * pixel-counts against, the same margin the 2026-08-30 retune gave the bands.
 */
export const TILE_ART_SAFE_BOX: ArtSafeBox = {
  x: CARD_W * 0.08,
  y: CARD_H * 0.135,
  w: CARD_W * 0.84,
  h: CARD_H * 0.32,
}

/**
 * Position an art crop inside a container of a known aspect ratio.
 *
 * @param aspect container width / height (2.5 for a 5:2 banner, 1 for a square)
 * @param box the card-pixel box the visible window must stay inside
 */
export function artCropStyle(aspect: number, box: ArtSafeBox = DEFAULT_ART_SAFE_BOX): CSSProperties {
  // A non-finite or non-positive aspect would produce NaN/Infinity percentages
  // and an image positioned nowhere. No caller does this today; fail to a plain
  // cover rather than to invisible geometry if one ever does.
  if (!Number.isFinite(aspect) || aspect <= 0) {
    return { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }
  }

  // The art box, inset on all four sides so an off-standard frame cannot show.
  const { x: safeX, w: safeW, y: safeY, h: safeH } = box

  // The window we want to show, in card pixels. Never wider than the art box;
  // when the container is proportionally taller than the art box, height is
  // the limit instead and the crop narrows.
  const windowW = Math.min(safeW, safeH * aspect)
  const windowH = windowW / aspect

  // Scale so `windowW` card pixels span the container's width.
  const widthPct = (CARD_W / windowW) * 100
  const leftPct = -((safeX + (safeW - windowW) / 2) / windowW) * 100

  // Rendered image height as a multiple of the container's HEIGHT.
  const imageHeightInContainers = (widthPct / 100) * CARD_RATIO * aspect
  // Top of the window, centered vertically on the art box.
  const windowTop = safeY + (safeH - windowH) / 2
  const topPct = -((windowTop / CARD_H) * imageHeightInContainers) * 100

  return {
    position: 'absolute',
    width: `${widthPct}%`,
    height: 'auto',
    maxWidth: 'none',
    left: `${leftPct}%`,
    top: `${topPct}%`,
  }
}

/** The two crops this app uses. Computed once; neither depends on runtime state. */
export const BANNER_ART_CROP = artCropStyle(5 / 2)
export const SQUARE_ART_CROP = artCropStyle(1)

// --- Background-image and img-element crop windows (formerly components/builder/cardArtCrop.ts) ---

/**
 * CSS crop windows onto a card's art box.
 *
 * POST /cards/images serves whole card images (`image_normal` / `image_large`),
 * not Scryfall art crops, so anything that wants just the illustration has to
 * crop it here. Rendering the whole card puts the frame, mana cost and printed
 * type line inside the surface, where they collide with our own overlaid text.
 *
 * The numbers come from a standard modern card face: the illustration spans
 * roughly 11%-47% of the card's height and about 86% of its width. Scaling the
 * background to 1/0.86 = ~116% of the container width therefore pushes the
 * frame's left and right edges outside the box entirely.
 *
 * The vertical offsets differ because the two surfaces have very different
 * aspect ratios, and a background-position percentage resolves against the
 * OVERFLOW, not the image - one shared number would drift the art band off
 * centre on one of them.
 *
 * Unusual layouts (full-art lands, sagas, some borderless printings) crop
 * imperfectly. That is accepted: in both places the art is decoration and the
 * card's name is the payload, rendered as real text on top.
 */
export interface ArtCropStyle {
  backgroundImage: string
  backgroundSize: string
  backgroundPosition: string
  backgroundRepeat: 'no-repeat'
}

function crop(url: string, size: string, position: string): ArtCropStyle {
  return {
    // Quoted: card names reach these URLs percent-encoded, but a bare url()
    // with an unquoted value is fragile against characters like parentheses.
    backgroundImage: `url("${url}")`,
    backgroundSize: size,
    backgroundPosition: position,
    backgroundRepeat: 'no-repeat',
  }
}

/**
 * Wide banner (roughly 2:1 to 6:1). Shows a horizontal slice of the art.
 *
 * 124% rather than the exact 116%: printings vary by a few pixels, and a
 * leftover sliver of gold border down the edge of a full-bleed banner is far
 * more noticeable than losing 3% of the illustration. At 124% the visible band
 * runs about 24%-33% of card height at desktop and 19%-47% at 390 - inside the
 * art box (11%-47%) at both ends.
 */
export function bannerArtCrop(url: string): ArtCropStyle {
  return crop(url, '124% auto', '50% 26%')
}

/**
 * 5:4 tile. Needs a MUCH tighter zoom than the banner, and the two were briefly
 * unified at 124% by mistake.
 *
 * The geometry does not forgive here: at 124% a 5:4 box exposes 46% of the
 * card's height, but the art box is only 36% tall, so ten points of frame had
 * to show and the printed type line sat inside every tile (masked only by the
 * name scrim). Do not re-unify these two numbers - they answer different
 * container aspects.
 *
 * 180%/20% (was 159%/17%): the old band ran ~11.2%-47.2% of card height,
 * flush against the NOMINAL art box (11%-47%) with zero margin, and real
 * printings vary by a point or two - so many tiles showed a thin sliver of
 * gold frame above the art (reported 2026-08-30). The new band is
 * ~13.6%-45.5%, ~2.5 points inside the nominal box at both ends.
 */
export function tileArtCrop(url: string): ArtCropStyle {
  return crop(url, '180% auto', '50% 20%')
}

/**
 * Img-element variants of the crops above, for surfaces that need lazy
 * loading (CSS backgrounds cannot lazy-load). Same geometry family: the
 * width percentage zooms PAST the card frame's side borders (art spans only
 * ~86% of card width, so anything at or under ~116% shows gold edges), and
 * object-position picks the vertical art band. object-fit: cover ALONE is
 * never enough for whole-card images - at any container aspect it shows the
 * full card width, and at tall aspects most of the card face. That is the
 * 2026-08-15 "card pieces across the site" incident.
 *
 * The img must sit inside a `relative overflow-hidden` container that owns
 * the final shape (radius, border).
 *
 * Constants are validated per container aspect with the synthetic-card
 * geometry harness (green art box / red frame, rendered and pixel-counted
 * in Chromium) - like the background variants, they answer specific
 * aspects and must not be reused for new ones without re-validating:
 *   bandImgArtCrop     h-24 band, cell widths 114-300px        (190%, 20%)
 *   halfBandImgArtCrop half of that band, halves 55-148px wide (390%, 20%)
 *   (the 4:3 /decks tile crops were retired 2026-09-06; the 2026-10-16
 *    CardStack deck tiles crop again, via artCropStyle + TILE_ART_SAFE_BOX,
 *    and are validated as the deck-solo / deck-half cases)
 *
 * The two remaining band constants were retuned 2026-08-30 for the same
 * reason as tileArtCrop above:
 * the previous constants (159/298/170/360 at posY 17-17.5) put the top of the
 * visible band at ~11.2% of card height, flush against the nominal art-box
 * top (11%), and printing variance made a thin gold frame sliver show above
 * the art on many tiles. Each band now sits ~2.5 points inside the nominal
 * box at both ends (validated against a 12.5%-46.5% "safe" box).
 *
 * The h-24 band's aspect FLOATS with cell width (unlike the aspect-locked
 * tiles), so its constants are sized for the worst real cell - grid-cols-2
 * at a 320px viewport - and merely crop tighter at desktop widths.
 */
export type ImgArtCropStyle = CSSProperties

function imgCrop(widthPct: number, posY: number): ImgArtCropStyle {
  return {
    width: `${widthPct}%`,
    height: '100%',
    maxWidth: 'none',
    position: 'absolute',
    // `top: 0` is LOAD-BEARING and was missing until 2026-09-08. An absolutely
    // positioned box with no `top` falls back to its STATIC position - where it
    // would have sat in normal flow - and that is only the container's top edge
    // for some containers. Every caller here happened to use a <span>, where it
    // is 0, so this read as unnecessary. CommanderHero's banner hosts the image
    // in a <button>, whose UA styling centres its content: the static top came
    // out at 46px inside a 92px strip, so the art rendered half below its own
    // clip and the banner showed an empty band above a sliver of card. Pinning
    // it makes the crop correct in ANY host rather than in the ones tried so
    // far; it is a no-op for the <span> callers, whose static top is already 0.
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    objectFit: 'cover',
    objectPosition: `center ${posY}%`,
  }
}

export const bandImgArtCrop = imgCrop(190, 20)
export const halfBandImgArtCrop = imgCrop(390, 20)

/**
 * The commander HERO BANNER (CommanderHero variant="banner"), a ~298x92 strip
 * in the report rail - or ~149x92 per commander when a partner splits it.
 *
 * It gets its own constants and MUST NOT reuse `bandImgArtCrop`. That was
 * tried and shipped on 2026-09-08 and made the banner materially worse: 190%
 * is sized for RecentDeckTile's WORST (narrowest) cell and, as the block
 * comment above says in as many words, "merely crops tighter at desktop
 * widths". Tighter is fine on a 114px home tile. On the 298px rail it leaves
 * roughly 12% of the card's height in frame - a sliver that cut every
 * commander in half. Keith, same day: "Made it WAY worse. Every banner cuts
 * off halfway now."
 *
 * 124% is the width the `bannerArtCrop` doc above already derived for exactly
 * this container shape ("roughly 2:1 to 6:1"): enough to push the frame's
 * side borders out of shot with margin for printing variance, and no more.
 * At 298x92 that keeps ~18% of card height visible against the old bare
 * cover's ~22%, so the borders go without the subject going with them.
 *
 * 21% centres that window near the art box's middle (~26.5% of card height)
 * across this container's whole live aspect range - 149px split, 298px rail,
 * and the full-width single column below `lg`. Verified in Chromium against
 * seven real printings spanning the modern frame, the 1997 border and the
 * borderless "inverted" commander frame.
 */
export const heroBannerImgArtCrop = imgCrop(124, 21)
